/**
 * Payment-history helpers — the pure half of the invoices modal.
 *
 * Everything the row rendering needs to decide (what to call a payment, which
 * amount is the real one, where the download points, how to word a status) is
 * here rather than inline in the JSX, so it can be unit tested without mounting
 * anything. `PaymentHistoryModal` is then just layout over these.
 *
 * Amounts arrive in CENTS from `GET /api/billing/invoices` (every billing
 * payload in this app does) — format them with `formatCents` from
 * `billingFormat`, never by dividing here.
 */

export interface Invoice {
  id: string
  /** Stripe's human invoice number, or the id for a draft that has none yet. */
  number: string
  status: string | null
  /** First line item's description — where the plan name actually lives. */
  description: string | null
  billing_reason: string | null
  amount_paid: number
  amount_due: number
  total: number
  /** ISO 4217, upper case. */
  currency: string
  created_at: string | null
  period_start: string | null
  period_end: string | null
  invoice_pdf: string | null
  hosted_invoice_url: string | null
}

export interface InvoicePage {
  invoices: Invoice[]
  has_more: boolean
}

/** Stripe's `billing_reason`, in the words a customer would use. */
const BILLING_REASON_LABELS: Record<string, string> = {
  subscription_create: 'Subscription started',
  subscription_cycle: 'Subscription renewal',
  subscription_update: 'Plan or seat change',
  subscription_threshold: 'Usage threshold charge',
  subscription: 'Subscription',
  manual: 'One-time charge',
  upcoming: 'Upcoming',
}

const STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  open: 'Due',
  draft: 'Draft',
  void: 'Void',
  uncollectible: 'Unpaid',
}

/**
 * Chip classes per status, dual-mode, matching the plan-status chips on the
 * billing page (colour lives in the chip and nowhere else).
 */
export const INVOICE_STATUS_CHIP: Record<string, string> = {
  paid: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25',
  open: 'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25',
  uncollectible:
    'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
}

const NEUTRAL_CHIP =
  'bg-muted text-muted-foreground border-border/60 dark:bg-white/[0.06] dark:text-[#8a8f98] dark:border-white/[0.08]'

export function invoiceStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown'
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ')
}

export function invoiceStatusChipClass(status: string | null | undefined): string {
  return (status && INVOICE_STATUS_CHIP[status]) || NEUTRAL_CHIP
}

/**
 * What this payment was FOR. Stripe's line-item description is the richest
 * ("1 x Momentum (at $129.00 / month)"); `billing_reason` is the fallback for
 * an invoice with no usable line, and a generic word is the last resort so a
 * row never renders an empty primary cell.
 */
export function invoiceLabel(invoice: Invoice): string {
  const described = invoice.description?.trim()
  if (described) return described
  const reason = invoice.billing_reason
  if (reason && BILLING_REASON_LABELS[reason]) return BILLING_REASON_LABELS[reason]
  return 'Payment'
}

/**
 * The amount to show on the row.
 *
 * A paid invoice's headline is what was actually collected — which is NOT
 * `total` when a coupon, credit balance or proration was applied. Anything not
 * yet paid has collected nothing, so it shows what is owed instead.
 */
export function invoiceAmountCents(invoice: Invoice): number {
  if (invoice.status === 'paid') return invoice.amount_paid
  return invoice.amount_due || invoice.total
}

/**
 * Where the Download button points: the direct PDF when Stripe has generated
 * one, otherwise the hosted invoice page (which offers its own download). A
 * draft has neither — the caller renders the button disabled on `null`.
 */
export function invoiceDownloadUrl(invoice: Invoice): string | null {
  return invoice.invoice_pdf || invoice.hosted_invoice_url || null
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "8 Sep 2026". Formatted in UTC from explicit parts rather than through
 * `toLocaleDateString`, so the output is identical in every browser locale and
 * in the test runner — invoices are day-granular, so the timezone shift that
 * buys determinism costs nothing meaningful.
 */
export function formatInvoiceDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/**
 * The service window an invoice covers — "5 Aug 2026 – 5 Sep 2026". Returns
 * null when either bound is missing, so the caller can omit the line entirely
 * rather than print a half-range.
 */
export function formatInvoicePeriod(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  if (!start || !end) return null
  const from = formatInvoiceDate(start)
  const to = formatInvoiceDate(end)
  if (from === '—' || to === '—') return null
  return `${from} – ${to}`
}
